import React, { useState, useEffect, useMemo } from 'react';
import {
  Plus, Trash2, Edit2, Save, X, Search, Filter,
  FileText, Users, Clock, Grid, AlertTriangle, 
  CheckCircle, Upload, Download, Copy
} from 'lucide-react';
import PageHeader from '../../components/layout/PageHeader';
import Button from '../../components/ui/Button';
import Card from '../../components/ui/Card';
import Spinner from '../../components/ui/Spinner';
import { useToast } from '../../contexts/ToastContext';
import { useConfirm } from '../../contexts/ConfirmContext';
import { useMultiGrid } from '../../contexts/MultiGridContext';

interface ClassData {
  id: string;
  className: string;
  subject: string;
  yearLevel: string;
  studentCount: number;
  periods: number;
  lineAssignments: { [lineId: string]: string };
  gridId: string;
  gridName: string;
}

const ClassManagement: React.FC = () => {
  const { addToast } = useToast();
  const confirm = useConfirm();
  const { allGrids: grids, isLoading: gridsLoading, updateGrid } = useMultiGrid();
  
  // Since MultiGridContext uses real-time listeners, grids auto-update
  // Create a simple refresh function that doesn't need to do anything
  const refreshGrids = async () => {
    // Real-time updates handled by MultiGridContext
    addToast('Grids updated automatically', 'info');
  };
  
  const [selectedGrid, setSelectedGrid] = useState<string>('');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterSubject, setFilterSubject] = useState('');
  const [filterYearLevel, setFilterYearLevel] = useState('');
  const [selectedClasses, setSelectedClasses] = useState<Set<string>>(new Set());
  const [editingClass, setEditingClass] = useState<string | null>(null);
  const [editedData, setEditedData] = useState<Partial<ClassData>>({});
  const [showAddForm, setShowAddForm] = useState(false);
  const [newClassData, setNewClassData] = useState({
    className: '',
    subject: '',
    yearLevel: '',
    studentCount: 0,
    periods: 5
  });

  // Get active grids
  const activeGrids = useMemo(() => {
    return grids.filter(g => g.isActive);
  }, [grids]);

  // Get all classes from selected grid or all grids
  const allClasses = useMemo(() => {
    const classes: ClassData[] = [];
    
    const gridsToProcess = selectedGrid 
      ? grids.filter(g => g.id === selectedGrid)
      : activeGrids;
    
    gridsToProcess.forEach(grid => {
      if (grid.classes) {
        grid.classes.forEach(cls => {
          classes.push({
            id: cls.id,
            className: cls.className,
            subject: cls.subject,
            yearLevel: cls.yearLevel,
            studentCount: cls.studentCount || 0,
            periods: cls.periods || 5,
            lineAssignments: cls.lineAssignments || {},
            gridId: grid.id,
            gridName: grid.name
          });
        });
      }
    });
    
    return classes;
  }, [grids, activeGrids, selectedGrid]);

  // Get unique subjects and year levels for filters
  const { subjects, yearLevels } = useMemo(() => {
    const subjectSet = new Set<string>();
    const yearLevelSet = new Set<string>();
    
    allClasses.forEach(cls => {
      if (cls.subject) subjectSet.add(cls.subject);
      if (cls.yearLevel) yearLevelSet.add(cls.yearLevel);
    });
    
    return {
      subjects: Array.from(subjectSet).sort(),
      yearLevels: Array.from(yearLevelSet).sort()
    };
  }, [allClasses]);

  // Filter classes based on search and filters
  const filteredClasses = useMemo(() => {
    return allClasses.filter(cls => {
      const matchesSearch = !searchTerm || 
        cls.className.toLowerCase().includes(searchTerm.toLowerCase()) ||
        cls.subject.toLowerCase().includes(searchTerm.toLowerCase());
      
      const matchesSubject = !filterSubject || cls.subject === filterSubject;
      const matchesYear = !filterYearLevel || cls.yearLevel === filterYearLevel;
      
      return matchesSearch && matchesSubject && matchesYear;
    });
  }, [allClasses, searchTerm, filterSubject, filterYearLevel]);

  // Add new class to grid
  const handleAddClass = async () => {
    if (!selectedGrid) {
      addToast('Please select a grid first', 'error');
      return;
    }

    if (!newClassData.className || !newClassData.subject) {
      addToast('Please fill in all required fields', 'error');
      return;
    }

    try {
      const grid = grids.find(g => g.id === selectedGrid);
      if (!grid) {
        addToast('Grid not found', 'error');
        return;
      }

      const newClass = {
        id: `${newClassData.className.replace(/\s+/g, '_')}_${Date.now()}`,
        className: newClassData.className,
        subject: newClassData.subject,
        yearLevel: newClassData.yearLevel,
        studentCount: newClassData.studentCount,
        periods: newClassData.periods,
        lineAssignments: {},
        curriculumArea: '' // Will be set based on subject mapping
      };

      const updatedClasses = [...(grid.classes || []), newClass];
      
      await updateGrid(selectedGrid, {
        classes: updatedClasses
      });
      addToast(`Added class ${newClassData.className} to ${grid.name}`, 'success');
      
      // Reset form
      setNewClassData({
        className: '',
        subject: '',
        yearLevel: '',
        studentCount: 0,
        periods: 5
      });
      setShowAddForm(false);
    } catch (error) {
      console.error('Error adding class:', error);
      addToast('Failed to add class', 'error');
    }
  };

  // Delete selected classes
  const handleDeleteClasses = async () => {
    if (selectedClasses.size === 0) {
      addToast('Please select classes to delete', 'error');
      return;
    }

    const confirmDelete = await confirm({
      title: `Delete ${selectedClasses.size} class${selectedClasses.size > 1 ? 'es' : ''}?`,
      message: `Are you sure you want to delete ${selectedClasses.size} class${selectedClasses.size > 1 ? 'es' : ''}? This cannot be undone.`,
      confirmLabel: 'Delete',
      variant: 'danger',
    });

    if (!confirmDelete) return;

    try {
      // Group classes by grid
      const classesByGrid = new Map<string, string[]>();
      selectedClasses.forEach(classId => {
        const cls = allClasses.find(c => c.id === classId);
        if (cls) {
          if (!classesByGrid.has(cls.gridId)) {
            classesByGrid.set(cls.gridId, []);
          }
          classesByGrid.get(cls.gridId)!.push(classId);
        }
      });

      // Update each grid
      for (const [gridId, classIds] of classesByGrid) {
        const grid = grids.find(g => g.id === gridId);
        if (grid) {
          const updatedClasses = grid.classes.filter(
            cls => !classIds.includes(cls.id)
          );
          
          await updateGrid(gridId, {
            classes: updatedClasses
          });
        }
      }

      addToast(`Deleted ${selectedClasses.size} class${selectedClasses.size > 1 ? 'es' : ''}`, 'success');
      setSelectedClasses(new Set());
    } catch (error) {
      console.error('Error deleting classes:', error);
      addToast('Failed to delete classes', 'error');
    }
  };

  // Save edited class
  const handleSaveEdit = async () => {
    if (!editingClass || !editedData) return;

    try {
      const cls = allClasses.find(c => c.id === editingClass);
      if (!cls) return;

      const grid = grids.find(g => g.id === cls.gridId);
      if (!grid) return;

      const updatedClasses = grid.classes.map(c => {
        if (c.id === editingClass) {
          return {
            ...c,
            ...editedData
          };
        }
        return c;
      });

      await updateGrid(cls.gridId, {
        classes: updatedClasses
      });
      addToast('Class updated successfully', 'success');
      setEditingClass(null);
      setEditedData({});
    } catch (error) {
      console.error('Error updating class:', error);
      addToast('Failed to update class', 'error');
    }
  };

  // Duplicate selected classes
  const handleDuplicateClasses = async () => {
    if (selectedClasses.size === 0) {
      addToast('Please select classes to duplicate', 'error');
      return;
    }

    try {
      const classesByGrid = new Map<string, any[]>();
      
      selectedClasses.forEach(classId => {
        const cls = allClasses.find(c => c.id === classId);
        if (cls) {
          const grid = grids.find(g => g.id === cls.gridId);
          if (grid) {
            if (!classesByGrid.has(cls.gridId)) {
              classesByGrid.set(cls.gridId, [...grid.classes]);
            }
            
            const gridClasses = classesByGrid.get(cls.gridId)!;
            const originalClass = grid.classes.find(c => c.id === classId);
            if (originalClass) {
              const duplicatedClass = {
                ...originalClass,
                id: `${originalClass.className}_copy_${Date.now()}`,
                className: `${originalClass.className} (Copy)`,
                lineAssignments: {} // Reset line assignments for duplicate
              };
              gridClasses.push(duplicatedClass);
            }
          }
        }
      });

      // Update each grid with duplicated classes
      for (const [gridId, updatedClasses] of classesByGrid) {
        await updateGrid(gridId, {
          classes: updatedClasses
        });
      }
      addToast(`Duplicated ${selectedClasses.size} class${selectedClasses.size > 1 ? 'es' : ''}`, 'success');
      setSelectedClasses(new Set());
    } catch (error) {
      console.error('Error duplicating classes:', error);
      addToast('Failed to duplicate classes', 'error');
    }
  };

  // Export classes to CSV
  const handleExportClasses = () => {
    const headers = ['Class Name', 'Subject', 'Year Level', 'Student Count', 'Periods', 'Grid'];
    const csvData = filteredClasses.map(cls => [
      cls.className,
      cls.subject,
      cls.yearLevel,
      cls.studentCount.toString(),
      cls.periods.toString(),
      cls.gridName
    ]);

    const csvContent = [headers, ...csvData]
      .map(row => row.map(cell => `"${cell}"`).join(','))
      .join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `classes_export_${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  if (gridsLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title="Class List"
        description="Add, edit, or remove individual classes from grids"
        actions={
          <div className="flex space-x-2">
            <Button
              variant="secondary"
              leftIcon={<Download className="h-4 w-4" />}
              onClick={handleExportClasses}
              disabled={filteredClasses.length === 0}
            >
              Export CSV
            </Button>
            <Button
              variant="primary"
              leftIcon={<Plus className="h-4 w-4" />}
              onClick={() => setShowAddForm(true)}
              disabled={!selectedGrid}
            >
              Add Class
            </Button>
          </div>
        }
      />

      <div className="space-y-6">
        {/* Grid Selector and Stats */}
        <Card>
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-ink-700 mb-2">
                  Select Grid
                </label>
                <select
                  value={selectedGrid}
                  onChange={(e) => setSelectedGrid(e.target.value)}
                  className="w-full border-ink-300 rounded-md shadow-sm focus:ring-navy-500 focus:border-navy-500"
                >
                  <option value="">All Active Grids</option>
                  {activeGrids.map(grid => (
                    <option key={grid.id} value={grid.id}>
                      {grid.name} ({grid.classes?.length || 0} classes)
                    </option>
                  ))}
                </select>
              </div>
              
              <div className="grid grid-cols-3 gap-4">
                <div className="text-center">
                  <div className="font-mono tabular-nums text-2xl font-bold text-navy-600">
                    {filteredClasses.length}
                  </div>
                  <div className="text-sm text-ink-500">Classes</div>
                </div>
                <div className="text-center">
                  <div className="font-mono tabular-nums text-2xl font-bold text-ok-fg">
                    {subjects.length}
                  </div>
                  <div className="text-sm text-ink-500">Subjects</div>
                </div>
                <div className="text-center">
                  <div className="font-mono tabular-nums text-2xl font-bold text-navy-600">
                    {filteredClasses.reduce((sum, cls) => sum + cls.studentCount, 0)}
                  </div>
                  <div className="text-sm text-ink-500">Students</div>
                </div>
              </div>
            </div>
          </div>
        </Card>

        {/* Filters */}
        <Card>
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-ink-400" />
                <input
                  type="text"
                  placeholder="Search classes..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 w-full border-ink-300 rounded-md shadow-sm focus:ring-navy-500 focus:border-navy-500"
                />
              </div>
              
              <select
                value={filterSubject}
                onChange={(e) => setFilterSubject(e.target.value)}
                className="w-full border-ink-300 rounded-md shadow-sm focus:ring-navy-500 focus:border-navy-500"
              >
                <option value="">All Subjects</option>
                {subjects.map(subject => (
                  <option key={subject} value={subject}>{subject}</option>
                ))}
              </select>
              
              <select
                value={filterYearLevel}
                onChange={(e) => setFilterYearLevel(e.target.value)}
                className="w-full border-ink-300 rounded-md shadow-sm focus:ring-navy-500 focus:border-navy-500"
              >
                <option value="">All Year Levels</option>
                {yearLevels.map(year => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </select>
              
              <div className="flex space-x-2">
                <Button
                  variant="secondary"
                  size="sm"
                  leftIcon={<Copy className="h-4 w-4" />}
                  onClick={handleDuplicateClasses}
                  disabled={selectedClasses.size === 0}
                >
                  Duplicate
                </Button>
                <Button
                  variant="danger"
                  size="sm"
                  leftIcon={<Trash2 className="h-4 w-4" />}
                  onClick={handleDeleteClasses}
                  disabled={selectedClasses.size === 0}
                >
                  Delete
                </Button>
              </div>
            </div>
          </div>
        </Card>

        {/* Add Class Form */}
        {showAddForm && (
          <Card>
            <div className="p-6">
              <h3 className="text-card-title text-ink-900 mb-4">Add New Class</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-ink-700 mb-1">
                    Class Name *
                  </label>
                  <input
                    type="text"
                    value={newClassData.className}
                    onChange={(e) => setNewClassData({ ...newClassData, className: e.target.value })}
                    className="w-full border-ink-300 rounded-md shadow-sm focus:ring-navy-500 focus:border-navy-500"
                    placeholder="e.g., 7A Mathematics"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-ink-700 mb-1">
                    Subject *
                  </label>
                  <input
                    type="text"
                    value={newClassData.subject}
                    onChange={(e) => setNewClassData({ ...newClassData, subject: e.target.value })}
                    className="w-full border-ink-300 rounded-md shadow-sm focus:ring-navy-500 focus:border-navy-500"
                    placeholder="e.g., Mathematics"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-ink-700 mb-1">
                    Year Level
                  </label>
                  <input
                    type="text"
                    value={newClassData.yearLevel}
                    onChange={(e) => setNewClassData({ ...newClassData, yearLevel: e.target.value })}
                    className="w-full border-ink-300 rounded-md shadow-sm focus:ring-navy-500 focus:border-navy-500"
                    placeholder="e.g., Year 7"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-ink-700 mb-1">
                    Student Count
                  </label>
                  <input
                    type="number"
                    value={newClassData.studentCount}
                    onChange={(e) => setNewClassData({ ...newClassData, studentCount: parseInt(e.target.value) || 0 })}
                    className="w-full border-ink-300 rounded-md shadow-sm focus:ring-navy-500 focus:border-navy-500"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-ink-700 mb-1">
                    Periods per Cycle
                  </label>
                  <input
                    type="number"
                    value={newClassData.periods}
                    onChange={(e) => setNewClassData({ ...newClassData, periods: parseInt(e.target.value) || 5 })}
                    className="w-full border-ink-300 rounded-md shadow-sm focus:ring-navy-500 focus:border-navy-500"
                  />
                </div>
                
                <div className="flex items-end space-x-2">
                  <Button
                    variant="primary"
                    leftIcon={<CheckCircle className="h-4 w-4" />}
                    onClick={handleAddClass}
                  >
                    Add Class
                  </Button>
                  <Button
                    variant="secondary"
                    leftIcon={<X className="h-4 w-4" />}
                    onClick={() => {
                      setShowAddForm(false);
                      setNewClassData({
                        className: '',
                        subject: '',
                        yearLevel: '',
                        studentCount: 0,
                        periods: 5
                      });
                    }}
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            </div>
          </Card>
        )}

        {/* Classes Table */}
        <Card>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-ink-200">
              <thead className="bg-ink-50">
                <tr>
                  <th className="px-6 py-3 text-left">
                    <input
                      type="checkbox"
                      checked={selectedClasses.size === filteredClasses.length && filteredClasses.length > 0}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedClasses(new Set(filteredClasses.map(c => c.id)));
                        } else {
                          setSelectedClasses(new Set());
                        }
                      }}
                      className="rounded border-ink-300 text-navy-600 focus:ring-navy-500"
                    />
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-ink-500 uppercase tracking-wider">
                    Class Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-ink-500 uppercase tracking-wider">
                    Subject
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-ink-500 uppercase tracking-wider">
                    Year Level
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-ink-500 uppercase tracking-wider">
                    Students
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-ink-500 uppercase tracking-wider">
                    Periods
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-ink-500 uppercase tracking-wider">
                    Grid
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-ink-500 uppercase tracking-wider">
                    Lines
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-ink-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-ink-200">
                {filteredClasses.map((cls) => (
                  <tr key={cls.id} className="hover:bg-ink-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <input
                        type="checkbox"
                        checked={selectedClasses.has(cls.id)}
                        onChange={(e) => {
                          const newSelected = new Set(selectedClasses);
                          if (e.target.checked) {
                            newSelected.add(cls.id);
                          } else {
                            newSelected.delete(cls.id);
                          }
                          setSelectedClasses(newSelected);
                        }}
                        className="rounded border-ink-300 text-navy-600 focus:ring-navy-500"
                      />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {editingClass === cls.id ? (
                        <input
                          type="text"
                          value={editedData.className || cls.className}
                          onChange={(e) => setEditedData({ ...editedData, className: e.target.value })}
                          className="w-full border-ink-300 rounded-md shadow-sm focus:ring-navy-500 focus:border-navy-500"
                        />
                      ) : (
                        <div className="text-sm font-medium text-ink-900">
                          {cls.className}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {editingClass === cls.id ? (
                        <input
                          type="text"
                          value={editedData.subject || cls.subject}
                          onChange={(e) => setEditedData({ ...editedData, subject: e.target.value })}
                          className="w-full border-ink-300 rounded-md shadow-sm focus:ring-navy-500 focus:border-navy-500"
                        />
                      ) : (
                        <div className="text-sm text-ink-900">{cls.subject}</div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {editingClass === cls.id ? (
                        <input
                          type="text"
                          value={editedData.yearLevel || cls.yearLevel}
                          onChange={(e) => setEditedData({ ...editedData, yearLevel: e.target.value })}
                          className="w-full border-ink-300 rounded-md shadow-sm focus:ring-navy-500 focus:border-navy-500"
                        />
                      ) : (
                        <div className="text-sm text-ink-900">{cls.yearLevel}</div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {editingClass === cls.id ? (
                        <input
                          type="number"
                          value={editedData.studentCount ?? cls.studentCount}
                          onChange={(e) => setEditedData({ ...editedData, studentCount: parseInt(e.target.value) || 0 })}
                          className="w-20 border-ink-300 rounded-md shadow-sm focus:ring-navy-500 focus:border-navy-500"
                        />
                      ) : (
                        <div className="font-mono tabular-nums text-sm text-ink-900">{cls.studentCount}</div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {editingClass === cls.id ? (
                        <input
                          type="number"
                          value={editedData.periods ?? cls.periods}
                          onChange={(e) => setEditedData({ ...editedData, periods: parseInt(e.target.value) || 5 })}
                          className="w-20 border-ink-300 rounded-md shadow-sm focus:ring-navy-500 focus:border-navy-500"
                        />
                      ) : (
                        <div className="font-mono tabular-nums text-sm text-ink-900">{cls.periods}</div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-ink-900">{cls.gridName}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="font-mono tabular-nums text-sm text-ink-500">
                        {Object.keys(cls.lineAssignments).length} assigned
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex space-x-2">
                        {editingClass === cls.id ? (
                          <>
                            <button
                              onClick={handleSaveEdit}
                              className="text-ok-fg hover:text-ok-fg"
                            >
                              <Save className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => {
                                setEditingClass(null);
                                setEditedData({});
                              }}
                              className="text-ink-500 hover:text-ink-900"
                            >
                              <X className="h-4 w-4" />
                            </button>
                          </>
                        ) : (
                          <>
                            <button
                              onClick={() => {
                                setEditingClass(cls.id);
                                setEditedData({});
                              }}
                              className="text-navy-600 hover:text-navy-900"
                            >
                              <Edit2 className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => {
                                setSelectedClasses(new Set([cls.id]));
                                handleDeleteClasses();
                              }}
                              className="text-danger-fg hover:text-danger-fg"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            
            {filteredClasses.length === 0 && (
              <div className="text-center py-12">
                <FileText className="h-12 w-12 text-ink-400 mx-auto mb-4" />
                <h3 className="text-sm font-medium text-ink-900">No classes found</h3>
                <p className="text-sm text-ink-500 mt-1">
                  {selectedGrid 
                    ? 'Add a class to get started'
                    : 'Select a grid to view classes'}
                </p>
              </div>
            )}
          </div>
        </Card>
      </div>
    </div>
  );
};

export default ClassManagement;